/**
 * The admin permission matrix — the single source of truth for both the
 * API gate (`apps/api/src/routes/admin.ts`) and the admin SPA navigation
 * (`apps/admin/src/components/AdminLayout.tsx`).
 *
 * Keeping it here, in a package both apps depend on, is what makes it
 * impossible for the menu to offer a section the server will refuse.
 *
 * `superadmin` is never stored in the database. It is derived from the
 * hardcoded email in `apps/api/src/lib/admin.ts` and therefore cannot be
 * granted, revoked, or lost.
 */

export type AdminRole = "superadmin" | "admin" | "manager";

export type AdminSection =
  | "overview"
  | "verifications"
  | "abuse_reports"
  | "users"
  | "entities"
  | "payments"
  | "coupons"
  | "marketing"
  | "backups"
  | "technical"
  | "events"
  | "admins"
  | "audit";

/** Commercial sections — the floor every admin role stands on. */
const MANAGER_SECTIONS = ["overview", "payments", "coupons"] as const;

/** Operational sections: real customer data, so managers stay out. */
const ADMIN_SECTIONS = [
  ...MANAGER_SECTIONS,
  "verifications",
  "abuse_reports",
  "users",
  "entities",
  // Marketing opt-outs carry free-text notes from real people, so they sit
  // with the operational sections rather than the commercial floor.
  "marketing",
] as const;

/** Technical and governance sections. */
const SUPERADMIN_SECTIONS = [
  ...ADMIN_SECTIONS,
  "backups",
  "technical",
  "events",
  "admins",
  "audit",
] as const;

export const ALL_SECTIONS: readonly AdminSection[] = SUPERADMIN_SECTIONS;

export const SECTIONS_BY_ROLE: Record<AdminRole, readonly AdminSection[]> = {
  superadmin: SUPERADMIN_SECTIONS,
  admin: ADMIN_SECTIONS,
  manager: MANAGER_SECTIONS,
};

/** Whether `role` may reach `section`. A missing role reaches nothing. */
export function can(
  role: AdminRole | null | undefined,
  section: AdminSection
): boolean {
  if (!role) return false;
  return SECTIONS_BY_ROLE[role].includes(section);
}

/**
 * Narrow an untrusted value (a D1 column, a JSON body) to an AdminRole.
 * D1 returns `null` for users with no admin access, so this doubles as
 * the "has any admin access at all" check.
 */
export function isAdminRole(value: unknown): value is AdminRole {
  return value === "superadmin" || value === "admin" || value === "manager";
}
